import { useIntl } from "react-intl";
import { Box, styled, SxProps, useTheme, Chip, lighten, darken } from "@mui/material";
import { Theme } from "@mui/system";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
	ActivityStatus,
	JourneyActivityType,
	UserJourneyActivityResponse,
	progressbarColor,
	statusBorderColor,
	iconColor,
	statusBgColor,
} from "user-journey/consts/journeyActivity";

import { formatDistance } from "localization/utils";

import { ReactComponent as CalendarIconSVG } from "assets/svg/calendar.svg";
import { ReactComponent as TimeIconSVG } from "assets/svg/time.svg";
import { ReactComponent as QuestionIconSVG } from "assets/svg/questions.svg";

import { getInvertColor } from "utils/themeUtils";

export const ActivityCard = styled("div")(
	({
		type,
		status,
		padding,
		bgcolor,
	}: {
		type?: JourneyActivityType;
		status: ActivityStatus;
		padding?: string;
		gap?: string;
		bgcolor?: string;
	}) => ({
		position: "relative",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",

		padding: padding || "2rem",
		width: "25.6rem",
		minWidth: "25.6rem",
		height: "30.5rem",
		backgroundColor:
			[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) || type === JourneyActivityType.GOAL
				? bgcolor
				: statusBgColor[status],
		border: `1px solid ${statusBorderColor[status]}`,
		borderRadius: "0.8rem",
	}),
);

export const ActivityChip = styled(Chip)(
	({
		status,
		type,
	}: {
		status: ActivityStatus;
		type?: JourneyActivityType.GOAL | JourneyActivityType.SETUP_GOAL;
	}) => {
		const theme = useTheme();

		const isLight = getInvertColor(theme.palette.primary.main) === "#3C4255";

		return {
			position: "absolute",
			top: 0,
			right: "2rem",
			transform: "translateY(-50%)",

			lineHeight: 1,
			fontWeight: 600,
			fontSize: "1.2rem",
			letterSpacing: "0.1px",
			color:
				[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) ||
				(status === ActivityStatus.IN_PROGRESS && type === JourneyActivityType.GOAL)
					? theme.palette.primary.main
					: "#FFF",
			backgroundColor:
				[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) ||
				(status === ActivityStatus.IN_PROGRESS && type === JourneyActivityType.GOAL)
					? isLight
						? darken(theme.palette.primary.main, 0.5)
						: lighten(theme.palette.primary.main, 0.9)
					: progressbarColor[status],
			borderRadius: "0.8rem",
		};
	},
);

export const ActivityStatusChip = ({
	status,
	type,
}: {
	status: ActivityStatus;
	type?: JourneyActivityType.GOAL | JourneyActivityType.SETUP_GOAL;
}) => {
	const intl = useIntl();

	const statusText = `USER-JOURNEY.activity.card.status.${status}`;

	return <ActivityChip status={status} type={type} label={intl.formatMessage({ id: statusText })} size="small" />;
};

export const ActivityHeader = styled("div")(({ theme }) => ({
	marginBottom: "1.2rem",
	width: "100%",
	textAlign: "center",
	color: theme.palette.grey["700"],

	"& h3": {
		marginTop: "0.4rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		fontWeight: 800,
		fontSize: "1.8rem",
		lineHeight: 1.2,
	},
	"& svg": {
		height: "2.9rem",

		"& .organization-color": {
			fill:
				getInvertColor(theme.palette.primary.main) === "#3C4255"
					? darken(theme.palette.primary.main, 0.5)
					: theme.palette.primary.main,
		},
	},
}));

export const MbtiResult = styled(Box)(({ status }: { status: ActivityStatus }) => {
	const theme = useTheme() as Theme;
	return {
		borderRadius: "8px",
		border: `2px dashed ${status === ActivityStatus.COMPLETED ? theme.palette.success["300"] : "#E0E0E0"}`,
		background: status === ActivityStatus.COMPLETED ? "#F6FBF5" : "transparent",
		width: "100%",
		marginTop: 4,

		"& .MuiTypography-root": {
			color: status === ActivityStatus.COMPLETED ? theme.palette.success.main : "inherit",
			textAlign: "center",
			fontSize: "1.4rem",
			fontStyle: "normal",
			fontWeight: 700,
			lineHeight: "22px",
		},
	};
});

export const CalendarIcon = styled(CalendarIconSVG)(
	({
		useRemUnit = false,
		status,
		type,
		svgColor,
	}: {
		status: ActivityStatus;
		useRemUnit?: boolean;
		type?: JourneyActivityType;
		svgColor?: string;
	}) => ({
		width: useRemUnit ? "1.5rem" : "15px",
		height: useRemUnit ? "1.5rem" : "15px",

		"& path": {
			fill:
				[ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) || type === JourneyActivityType.GOAL
					? svgColor
					: iconColor[status],
		},
	}),
);

export const QuestionMarkIcon = styled(QuestionIconSVG)(
	({
		useRemUnit = false,
		status,
		svgColor,
	}: {
		status: ActivityStatus;
		useRemUnit?: boolean;
		svgColor?: string;
	}) => ({
		width: useRemUnit ? "1.6rem" : "16px",
		height: useRemUnit ? "1.6rem" : "16px",

		"& path": {
			fill: [ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) ? svgColor : iconColor[status],
		},
	}),
);

export const TimeIcon = styled(TimeIconSVG)(({ status, svgColor }: { status: ActivityStatus; svgColor?: string }) => ({
	width: "1.6rem",
	height: "1.6rem",

	"& path": {
		fill: [ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status) ? svgColor : iconColor[status],
	},
}));

export function LockedSection({ activity, sx }: { activity: UserJourneyActivityResponse; sx?: SxProps }) {
	const intl = useIntl();
	const { lockedBy, lockedUntilDate } = activity;

	let lockedByString = "";

	if (lockedUntilDate && Date.now() < new Date(lockedUntilDate).getTime()) {
		const distance = formatDistance(new Date(lockedUntilDate), new Date(), { addSuffix: true });
		lockedByString = intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.open" }, { dateText: distance });
	}

	if (lockedBy.length > 0) {
		const total = lockedBy.length;
		const first = lockedBy[0];

		const prefix =
			first.type === JourneyActivityType.UPLOAD_FILE
				? intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.upload" })
				: intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.finish" });

		if (lockedByString) {
			lockedByString += ", ";
		}

		if (total === 1) {
			lockedByString += `${prefix} ${first.name}`;
		} else {
			lockedByString += `${prefix} ${first.name} ${intl.formatMessage({ id: "GLOBAL.and" })} ${
				total - 1
			} ${intl.formatMessage({ id: "GLOBAL.others" })}`;
		}
		lockedByString += " " + intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.to-open" });
	}

	return (
		<Box
			display="flex"
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			gap="0.4rem"
			m="1.6rem"
			p="1rem"
			textAlign="center"
			fontSize="1.5rem"
			border="2px dashed"
			borderColor="grey.200"
			borderRadius="0.8rem"
			sx={sx}
		>
			<InfoOutlinedIcon />
			{lockedByString}
		</Box>
	);
}
